import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";

import { API } from "@/lib/apiBase";
import { MathCaptcha, useCaptcha } from "@/components/MathCaptcha";

const ROLE_DESCRIPTIONS = {
  public: "Browse the catalog, save favorites. Pending admin approval after signup.",
  member: "Alfred Membership account — choose a paid plan below (Club / Premium / Business) for full access.",
  sommelier: "Everything in Public, plus submit wine credits (ratings) for admin approval.",
  partner: "Everything in Public, plus submit new wines to be added to the catalog.",
};

const MEMBERSHIP_DESCRIPTIONS = {
  none: "No membership plan — free account only.",
  club: "$25/mo or $249/yr (save 17%) · Full catalog ratings · Private Cellar up to 250 bottles.",
  premium: "$50/mo or $498/yr (save 17%) · All Club benefits · Marketplace access · Exclusive offers.",
  business: "Custom pricing — our team will contact you after signup.",
};

const PACKAGE_ID = { club: "club_yearly", premium: "premium_yearly" };

export default function Register() {
  const { register } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("public");
  const [membership, setMembership] = useState("none");
  const [credential, setCredential] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [loading, setLoading] = useState(false);
  const captcha = useCaptcha();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email || !password) return toast.error("Please fill in all fields");
    if (password.length < 6) return toast.error("Password must be at least 6 characters");
    if (role === "sommelier" && !credential.trim()) return toast.error("Sommelier accounts require a credential");
    if (role === "partner" && !businessName.trim()) return toast.error("Partner accounts require a business name");
    if (!captcha.answer) return toast.error("Please answer the security question");

    setLoading(true);
    try {
      await register({
        name,
        email,
        password,
        role,
        credential: credential || null,
        business_name: businessName || null,
        ...captcha.payload,
      });

      // Admin shortcut
      if (email.toLowerCase() === "alfredwinescom@gmail.com") {
        toast.success("Welcome back, admin");
        navigate("/catalog");
        return;
      }

      // Route based on chosen membership
      if (membership === "club" || membership === "premium") {
        toast.success("Account created. Redirecting to checkout…", { duration: 4000 });
        try {
          const res = await axios.post(`${API}/membership/checkout`, {
            package_id: PACKAGE_ID[membership],
            origin_url: window.location.origin,
          });
          window.location.href = res.data.url;
          return;
        } catch (err) {
          console.error("checkout failed", err);
          toast.error("Couldn't start checkout. You can subscribe later from the Membership page.");
          navigate("/membership");
          return;
        }
      }

      if (membership === "business") {
        toast.success("Account created. Our team will contact you about the Business plan.", { duration: 6000 });
        window.location.href =
          "mailto:tastings@alfredwines.com?subject=Business%20Membership%20Inquiry&body=" +
          encodeURIComponent(`Hi, I just created an account (${email}) and am interested in the Business plan. Please contact me.`);
        navigate("/profile");
        return;
      }

      toast.success("Account created. Awaiting admin approval.", { duration: 6000 });
      navigate("/profile");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Registration failed");
      captcha.refresh();
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="pt-24 pb-20 min-h-screen flex items-center justify-center" data-testid="register-page">
      <div className="w-full max-w-md px-6">
        <div className="text-center mb-10">
          <a href="https://alfredwine.com/">
            <img src="https://customer-assets.emergentagent.com/job_vineyard-price-scout/artifacts/lksk5cgy_Albert%20%281%29.png" alt="AlfredWine.com" className="h-8 object-contain mx-auto mb-6" />
          </a>
          <h1 className="font-display text-2xl text-white/90 mb-1">{t.createAccount}</h1>
          <p className="font-body text-sm text-white/40">{t.createTastingAccount}</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-5">
          <Field label={t.name}>
            <Input data-testid="register-name" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className={inputCls} />
          </Field>
          <Field label={t.email}>
            <Input data-testid="register-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" className={inputCls} />
          </Field>
          <Field label={t.password}>
            <Input data-testid="register-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min. 6 characters" className={inputCls} />
          </Field>

          <div>
            <Label className="font-body text-xs text-white/40 tracking-wider uppercase block mb-2">Account Type</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="register-role" className="bg-transparent border-white/10 text-white/90 h-11 rounded-sm font-body text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-wine-paper border-white/10">
                <SelectItem value="public" className="text-white/80 font-body text-sm">Public — Browse &amp; Favorite</SelectItem>
                <SelectItem value="member" className="text-gold font-body text-sm">Alfred Membership — Paid Plan</SelectItem>
                <SelectItem value="sommelier" className="text-white/80 font-body text-sm">Sommelier — Submit Credits</SelectItem>
                <SelectItem value="partner" className="text-white/80 font-body text-sm">Partner — Submit Wines</SelectItem>
              </SelectContent>
            </Select>
            <p className="font-body text-[11px] text-white/40 mt-2">{ROLE_DESCRIPTIONS[role]}</p>
          </div>

          {role === "sommelier" && (
            <Field label="Sommelier Credential">
              <Input
                data-testid="register-credential"
                type="text"
                value={credential}
                onChange={(e) => setCredential(e.target.value)}
                placeholder="e.g. WSET Level 3, Court of Master Sommeliers"
                className={inputCls}
              />
            </Field>
          )}
          {role === "partner" && (
            <Field label="Business / Winery Name">
              <Input
                data-testid="register-business"
                type="text"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                placeholder="e.g. Bodegas Muriel"
                className={inputCls}
              />
            </Field>
          )}

          <div>
            <Label className="font-body text-xs text-white/40 tracking-wider uppercase block mb-2">Membership Type</Label>
            <Select value={membership} onValueChange={setMembership}>
              <SelectTrigger data-testid="register-membership" className="bg-transparent border-white/10 text-white/90 h-11 rounded-sm font-body text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-wine-paper border-white/10">
                <SelectItem value="none" className="text-white/80 font-body text-sm">None — free account</SelectItem>
                <SelectItem value="club" className="text-white/80 font-body text-sm">Club — $25/mo · $249/yr</SelectItem>
                <SelectItem value="premium" className="text-white/80 font-body text-sm">Premium — $50/mo · $498/yr</SelectItem>
                <SelectItem value="business" className="text-white/80 font-body text-sm">Business — custom</SelectItem>
              </SelectContent>
            </Select>
            <p className="font-body text-[11px] text-white/40 mt-2">{MEMBERSHIP_DESCRIPTIONS[membership]}</p>
          </div>

          <MathCaptcha captcha={captcha} testPrefix="register-captcha" />

          <Button
            data-testid="register-submit"
            type="submit"
            disabled={loading}
            className="w-full bg-merlot hover:bg-merlot-light text-white font-body text-sm h-11 rounded-sm shadow-[0_0_15px_rgba(158,42,43,0.3)] transition-all duration-300"
          >
            {loading
              ? t.creatingAccount
              : (membership === "club" || membership === "premium")
                ? "Create Account & Continue to Payment"
                : membership === "business"
                  ? "Create Account & Contact Sales"
                  : t.createAccount}
          </Button>
        </form>
        <p className="font-body text-sm text-white/30 text-center mt-8">
          {t.alreadyHaveAccount}{" "}
          <Link to="/login" className="text-merlot-light hover:text-gold transition-colors" data-testid="goto-login">{t.signInLink}</Link>
        </p>
      </div>
    </main>
  );
}

const inputCls = "bg-transparent border-white/10 text-white/90 font-body text-sm placeholder:text-white/20 h-11 rounded-sm focus:border-merlot/50";

function Field({ label, children }) {
  return (
    <div>
      <Label className="font-body text-xs text-white/40 tracking-wider uppercase block mb-2">{label}</Label>
      {children}
    </div>
  );
}
